import classes from './PageHome.module.scss';
import apiClient from '@/api/client';
import { API_GET_INFERENCE_RESULTS_ENDPOINT } from '@/api/config';

import { Button, Skeleton, Text } from '@mantine/core';
import IconDatabase from '@tabler/icons-react/IconDatabase';
import IconFileAnalytics from '@tabler/icons-react/IconFileAnalytics';
import IconReportAnalytics from '@tabler/icons-react/IconReportAnalytics';
import IconUpload from '@tabler/icons-react/IconUpload';
import IconUsersGroup from '@tabler/icons-react/IconUsersGroup';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDisplayDateTime } from '@/utils/dateFormat';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface InferenceResultsSummaryResponse {
  data: Array<{
    uploaded_at: string | null;
  }>;
  total: number;
}

function formatApiDateTime(date: Date, boundary: 'start' | 'end'): string {
  const normalizedDate = new Date(date);
  if (boundary === 'start') {
    normalizedDate.setHours(0, 0, 0, 0);
  } else {
    normalizedDate.setHours(23, 59, 59, 999);
  }

  const year = normalizedDate.getFullYear();
  const month = String(normalizedDate.getMonth() + 1).padStart(2, '0');
  const day = String(normalizedDate.getDate()).padStart(2, '0');
  const hours = String(normalizedDate.getHours()).padStart(2, '0');
  const minutes = String(normalizedDate.getMinutes()).padStart(2, '0');
  const seconds = String(normalizedDate.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function getUploadDateTimeLabel(value: string | null): string | null {
  if (!value) {
    return null;
  }

  return formatDisplayDateTime(value);
}

export default function PageHome() {
  const { t } = useLanguage();
  const [latestResultsCount, setLatestResultsCount] = useState<number | null>(null);
  const [latestUploadAt, setLatestUploadAt] = useState<string | null>(null);
  const [latestResultsLoading, setLatestResultsLoading] = useState(true);
  const [latestResultsError, setLatestResultsError] = useState(false);

  const lastSevenDaysUploadRange = useMemo(() => {
    const today = new Date();
    const dateFrom = new Date(today);
    dateFrom.setDate(today.getDate() - 6);

    return {
      uploadedFrom: formatApiDateTime(dateFrom, 'start'),
      uploadedTo: formatApiDateTime(today, 'end'),
    };
  }, []);

  const latestResultsHref = `/inference_results?${new URLSearchParams({
    uploaded_from: lastSevenDaysUploadRange.uploadedFrom,
    uploaded_to: lastSevenDaysUploadRange.uploadedTo,
  }).toString()}`;
  const latestUploadDateTime = useMemo(
    () => getUploadDateTimeLabel(latestUploadAt),
    [latestUploadAt]
  );

  useEffect(() => {
    let active = true;

    const fetchLatestResultsCount = async () => {
      try {
        setLatestResultsLoading(true);
        setLatestResultsError(false);

        const response = await apiClient.get<InferenceResultsSummaryResponse>(
          API_GET_INFERENCE_RESULTS_ENDPOINT,
          {
            params: {
              limit: 1,
              offset: 0,
              uploaded_from: lastSevenDaysUploadRange.uploadedFrom,
              uploaded_to: lastSevenDaysUploadRange.uploadedTo,
              sort_by: 'uploaded_at',
              sort_order: 'desc',
            },
          }
        );

        if (active) {
          setLatestResultsCount(response.data.total);
          setLatestUploadAt(response.data.data[0]?.uploaded_at ?? null);
        }
      } catch (error) {
        console.error('Failed to load latest results count:', error);

        if (active) {
          setLatestResultsError(true);
          setLatestResultsCount(null);
          setLatestUploadAt(null);
        }
      } finally {
        if (active) {
          setLatestResultsLoading(false);
        }
      }
    };

    fetchLatestResultsCount();

    return () => {
      active = false;
    };
  }, [lastSevenDaysUploadRange]);

  return (
    <div className={classes.dashboardPage}>
      <div className={classes.dashboardShell}>
        <section className={classes.overviewBand}>
          <div className={classes.welcomeSurface}>
            <Text className={classes.eyebrow}>{t('dashboard.eyebrow')}</Text>
            <h1 className={classes.title}>{t('dashboard.title')}</h1>
            <Text className={classes.summary}>
              {t('dashboard.summary')}
            </Text>
          </div>

          <div className={classes.metricSurface}>
            <Text className={classes.metricLabel}>{t('dashboard.lastSevenDays')}</Text>
            <div className={classes.metricBody}>
              {latestResultsLoading ? (
                <Skeleton height={64} width={132} radius="sm" />
              ) : (
                <Text className={classes.metricValue}>
                  {latestResultsError ? '-' : latestResultsCount ?? 0}
                </Text>
              )}
              <Text className={classes.metricHint}>{t('dashboard.resultsAdded')}</Text>
            </div>
            <div className={classes.metricFooter}>
              {latestResultsLoading ? (
                <Skeleton height={14} width={190} radius="sm" />
              ) : latestResultsError ? (
                <Text className={classes.metricTimestamp}>
                  {t('dashboard.lastUploadUnavailable')}
                </Text>
              ) : latestUploadDateTime ? (
                <Text className={classes.metricTimestamp}>
                  {t('dashboard.lastUpload', {
                    dateTime: latestUploadDateTime,
                  })}
                </Text>
              ) : (
                <Text className={classes.metricTimestamp}>
                  {t('dashboard.noUploads')}
                </Text>
              )}
              <Button
                component={Link}
                to={latestResultsHref}
                color="green"
                className={classes.metricButton}
                leftSection={<IconFileAnalytics size={17} />}
              >
                {t('dashboard.seeResults')}
              </Button>
            </div>
          </div>
        </section>

        <section className={classes.actionGrid}>
          <div className={classes.actionSurface}>
            <div>
              <Text className={classes.actionTitle}>{t('dashboard.mudCheckTitle')}</Text>
              <Text className={classes.actionText}>
                {t('dashboard.mudCheckDescription')}
              </Text>
            </div>
            <Button
              component={Link}
              to="/add_inference"
              variant="light"
              color="green"
              className={classes.actionButton}
              leftSection={<IconUpload size={17} />}
            >
              {t('dashboard.startUpload')}
            </Button>
          </div>

          <div className={classes.actionSurface}>
            <div>
              <Text className={classes.actionTitle}>{t('dashboard.rigWellTitle')}</Text>
              <Text className={classes.actionText}>
                {t('dashboard.rigWellDescription')}
              </Text>
            </div>
            <Button
              component={Link}
              to="/rig_well"
              variant="light"
              color="green"
              className={classes.actionButton}
              leftSection={<IconUsersGroup size={17} />}
            >
              {t('dashboard.browseWells')}
            </Button>
          </div>

          <div className={classes.actionSurface}>
            <div>
              <Text className={classes.actionTitle}>{t('dashboard.importedFilesTitle')}</Text>
              <Text className={classes.actionText}>
                {t('dashboard.importedFilesDescription')}
              </Text>
            </div>
            <Button
              component={Link}
              to="/import_files"
              variant="light"
              color="green"
              className={classes.actionButton}
              leftSection={<IconDatabase size={17} />}
            >
              {t('dashboard.viewFiles')}
            </Button>
          </div>

          <div className={classes.actionSurface}>
            <div>
              <Text className={classes.actionTitle}>{t('centrifuge.reportsTitle')}</Text>
              <Text className={classes.actionText}>
                {t('dashboard.centrifugeReportsDescription')}
              </Text>
            </div>
            <Button
              component={Link}
              to="/centrifuge_reports"
              variant="light"
              color="green"
              className={classes.actionButton}
              leftSection={<IconReportAnalytics size={17} />}
            >
              {t('dashboard.viewCentrifugeReports')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
